/**
 * messagingPersistence.ts
 *
 * Single source of truth for all messaging state persistence.
 * Handles: shown today, shown this week, T2 first-crossing flags, one-time flags.
 *
 * All state is localStorage-based (date/week keyed — auto-resets).
 * One-time flags (A2HS) are Firestore-persisted via the caller.
 *
 * Target: src/services/messaging/messagingPersistence.ts
 */

import { getTodayDate } from '../../utils/date';
import type {
  MessageId,
  DailyMessageState,
  WeeklyMessageState,
} from './messaging.types';

// ── Storage keys ──────────────────────────────────────────────────────────────

const DAILY_STATE_KEY = 'coretrak.messaging.daily';
const WEEKLY_STATE_KEY = 'coretrak.messaging.weekly';
// Tracks which gap start date the recovery message has already fired for.
// Keyed by the date of the first missed day — resets when user logs again.
const RECOVERY_GAP_KEY = 'coretrak.messaging.recoveryGapDate';
// One-time flag — once on_track fires it never fires again.
const ON_TRACK_SHOWN_KEY = 'coretrak.messaging.onTrackShown';
const FIRST_PROGRESS_SHOWN_KEY = 'coretrak.messaging.firstProgressShown';
// One-time flag — once normative reassurance fires it never fires again.
const NORMATIVE_SHOWN_KEY = 'coretrak.messaging.normativeShown';
// A1 first-entry preview — one-time ever
const A1_FIRST_ENTRY_SHOWN_KEY = 'coretrak.messaging.a1FirstEntryPreview.shown';
const A1_FIRST_ENTRY_TYPE_KEY = 'coretrak.messaging.a1FirstEntryPreview.type';
// Pattern drop cooldown keys — 7-day per metric, 3-day global
const PATTERN_DROP_LOGGING_KEY = 'coretrak.messaging.patternDrop.logging.lastShownAt';
const PATTERN_DROP_PROTEIN_KEY = 'coretrak.messaging.patternDrop.protein.lastShownAt';
const PATTERN_DROP_WORKOUT_KEY = 'coretrak.messaging.patternDrop.workout.lastShownAt';
const PATTERN_DROP_ANY_KEY = 'coretrak.messaging.patternDrop.any.lastShownAt';

// ── ISO week helper ───────────────────────────────────────────────────────────

function getIsoWeekString(date: Date = new Date()): string {
  // Returns YYYY-Www for the Monday-based ISO week containing `date`
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  // Thursday in current week determines the year
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

// ── Daily state ───────────────────────────────────────────────────────────────

function getEmptyDailyState(): DailyMessageState {
  return {
    date: getTodayDate(),
    shownGeneral: [],
    recoveredToday: [],
    shownGlp1Meal: null,
  };
}

export function getDailyState(): DailyMessageState {
  try {
    const raw = localStorage.getItem(DAILY_STATE_KEY);
    if (!raw) return getEmptyDailyState();
    const parsed = JSON.parse(raw) as DailyMessageState;
    // Reset if date has rolled over
    if (parsed.date !== getTodayDate()) return getEmptyDailyState();
    return parsed;
  } catch {
    return getEmptyDailyState();
  }
}

function saveDailyState(state: DailyMessageState): void {
  try {
    state.date = getTodayDate(); // Always stamp with today
    localStorage.setItem(DAILY_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('[MessagingPersistence] Failed to save daily state:', error);
  }
}

// ── Weekly state ──────────────────────────────────────────────────────────────

function getEmptyWeeklyState(): WeeklyMessageState {
  return {
    week: getIsoWeekString(),
    shownWeekly: null,
  };
}

export function getWeeklyState(): WeeklyMessageState {
  try {
    const raw = localStorage.getItem(WEEKLY_STATE_KEY);
    if (!raw) return getEmptyWeeklyState();
    const parsed = JSON.parse(raw) as WeeklyMessageState;
    // Reset if week has rolled over
    if (parsed.week !== getIsoWeekString()) return getEmptyWeeklyState();
    return parsed;
  } catch {
    return getEmptyWeeklyState();
  }
}

function saveWeeklyState(state: WeeklyMessageState): void {
  try {
    state.week = getIsoWeekString();
    localStorage.setItem(WEEKLY_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('[MessagingPersistence] Failed to save weekly state:', error);
  }
}

// ── Public API — daily ────────────────────────────────────────────────────────

/** True if any general message (T1/T2/T3) has been shown today. */
export function hasGeneralMessageShownToday(): boolean {
  return getDailyState().shownGeneral.length > 0;
}

/** True if this specific general message has been shown today. */
export function hasMessageShownToday(id: MessageId): boolean {
  return getDailyState().shownGeneral.includes(id);
}

/** Mark a general message as shown today. */
export function markGeneralMessageShown(id: MessageId): void {
  const state = getDailyState();
  if (!state.shownGeneral.includes(id)) {
    state.shownGeneral.push(id);
    saveDailyState(state);
  }
}

/**
 * T2 only: Mark that this threshold has been crossed today.
 * Prevents re-firing if data is re-evaluated later in the day.
 */
export function markRecoveredToday(id: MessageId): void {
  const state = getDailyState();
  if (!state.recoveredToday.includes(id)) {
    state.recoveredToday.push(id);
    saveDailyState(state);
  }
}

/** T2 only: True if this threshold was already crossed today. */
export function hasRecoveredToday(id: MessageId): boolean {
  return getDailyState().recoveredToday.includes(id);
}

/** True if a GLP-1 meal nudge has been shown today. */
export function hasGlp1MealShownToday(): boolean {
  return getDailyState().shownGlp1Meal !== null;
}

/** Mark a GLP-1 meal nudge as shown today. */
export function markGlp1MealShown(id: MessageId): void {
  const state = getDailyState();
  state.shownGlp1Meal = id;
  saveDailyState(state);
}

// ── Public API — weekly ───────────────────────────────────────────────────────

/** True if a T3 weekly message has been shown this week. */
export function hasWeeklyMessageShownThisWeek(): boolean {
  return getWeeklyState().shownWeekly !== null;
}

/** Mark a T3 weekly message as shown this week. */
export function markWeeklyMessageShown(id: MessageId): void {
  const state = getWeeklyState();
  state.shownWeekly = id;
  saveWeeklyState(state);
}

// ── Public API — on track (one-time) ─────────────────────────────────────────

/** True if the on-track message has ever been shown. */
export function hasOnTrackEverShown(): boolean {
  try { return localStorage.getItem(ON_TRACK_SHOWN_KEY) === 'true'; } catch { return false; }
}

/** Mark the on-track message as permanently shown. */
export function markOnTrackShown(): void {
  try { localStorage.setItem(ON_TRACK_SHOWN_KEY, 'true'); } catch { /* best effort */ }
}

// ── Public API — first progress (one-time) ───────────────────────────────────

/** True if the first progress signal has ever been shown. */
export function hasFirstProgressEverShown(): boolean {
  try { return localStorage.getItem(FIRST_PROGRESS_SHOWN_KEY) === 'true'; } catch { return false; }
}

/** Mark the first progress signal as permanently shown. */
export function markFirstProgressShown(): void {
  try { localStorage.setItem(FIRST_PROGRESS_SHOWN_KEY, 'true'); } catch { /* best effort */ }
}

// ── Public API — recovery gap ─────────────────────────────────────────────────

/** True if the recovery message has already fired for the gap starting on gapStartDate. */
export function hasRecoveryFiredForGap(gapStartDate: string): boolean {
  try {
    return localStorage.getItem(RECOVERY_GAP_KEY) === gapStartDate;
  } catch { return false; }
}

/** Mark the recovery message as fired for the gap starting on gapStartDate. */
export function markRecoveryFiredForGap(gapStartDate: string): void {
  try {
    localStorage.setItem(RECOVERY_GAP_KEY, gapStartDate);
  } catch { /* best effort */ }
}

/** Clear the recovery gap flag — call when user logs again to allow future gaps to fire. */
export function clearRecoveryGap(): void {
  try {
    localStorage.removeItem(RECOVERY_GAP_KEY);
    localStorage.removeItem(ON_TRACK_SHOWN_KEY);
    localStorage.removeItem(FIRST_PROGRESS_SHOWN_KEY);
    localStorage.removeItem(NORMATIVE_SHOWN_KEY);
    localStorage.removeItem(A1_FIRST_ENTRY_SHOWN_KEY);
    localStorage.removeItem(A1_FIRST_ENTRY_TYPE_KEY);
    localStorage.removeItem(PATTERN_DROP_LOGGING_KEY);
    localStorage.removeItem(PATTERN_DROP_PROTEIN_KEY);
    localStorage.removeItem(PATTERN_DROP_WORKOUT_KEY);
    localStorage.removeItem(PATTERN_DROP_ANY_KEY);
  } catch { /* best effort */ }
}

// ── Public API — normative reassurance (one-time ever) ──────────────────────

/** Reusable cooldown check — true if lastShownAt is within cooldownMs. */
export function hasShownWithinCooldown(lastShownAt: number | undefined, cooldownMs: number): boolean {
  if (!lastShownAt) return false;
  return Date.now() - lastShownAt < cooldownMs;
}

/** True if normative reassurance has ever been shown. */
export function hasNormativeReassuranceEverShown(): boolean {
  try { return localStorage.getItem(NORMATIVE_SHOWN_KEY) === 'true'; } catch { return false; }
}

/** @deprecated Use hasNormativeReassuranceEverShown. Kept for compatibility. */
export function hasNormativeReassuranceShownInLast3Days(): boolean {
  return hasNormativeReassuranceEverShown();
}

/** Mark normative reassurance as permanently shown — never shows again. */
export function markNormativeReassuranceShown(): void {
  try { localStorage.setItem(NORMATIVE_SHOWN_KEY, 'true'); } catch { /* best effort */ }
}

// ── Public API — A1 first-entry preview (one-time) ──────────────────────────

/** True if the A1 first-entry preview has ever been shown. */
export function hasA1FirstEntryShown(): boolean {
  try { return localStorage.getItem(A1_FIRST_ENTRY_SHOWN_KEY) === 'true'; } catch { return false; }
}

/** Mark A1 as shown. type = 'nutrition' | 'workout' for optional debug key. */
export function markA1FirstEntryShown(type: 'nutrition' | 'workout'): void {
  try {
    localStorage.setItem(A1_FIRST_ENTRY_SHOWN_KEY, 'true');
    localStorage.setItem(A1_FIRST_ENTRY_TYPE_KEY, type);
  } catch { /* best effort */ }
}

// ── Public API — pattern drop guidance (cooldown-based per metric) ───────────

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function getPatternDropLastShown(key: string): number | undefined {
  try { const r = localStorage.getItem(key); return r ? Number(r) : undefined; } catch { return undefined; }
}

/** True if pattern drop for this metric was shown within 7 days. */
export function hasPatternDropShownInLast7Days(metric: 'logging' | 'protein' | 'workout'): boolean {
  const key = metric === 'logging' ? PATTERN_DROP_LOGGING_KEY : metric === 'protein' ? PATTERN_DROP_PROTEIN_KEY : PATTERN_DROP_WORKOUT_KEY;
  return hasShownWithinCooldown(getPatternDropLastShown(key), SEVEN_DAYS_MS);
}

/** True if any pattern drop message was shown within 3 days (global cap). */
export function hasAnyPatternDropShownInLast3Days(): boolean {
  return hasShownWithinCooldown(getPatternDropLastShown(PATTERN_DROP_ANY_KEY), THREE_DAYS_MS);
}

/** Mark a pattern drop message as shown now. Updates both metric and global keys. */
export function markPatternDropShown(metric: 'logging' | 'protein' | 'workout'): void {
  try {
    const now = String(Date.now());
    const key = metric === 'logging' ? PATTERN_DROP_LOGGING_KEY : metric === 'protein' ? PATTERN_DROP_PROTEIN_KEY : PATTERN_DROP_WORKOUT_KEY;
    localStorage.setItem(key, now);
    localStorage.setItem(PATTERN_DROP_ANY_KEY, now);
  } catch { /* best effort */ }
}

// ── Utility ───────────────────────────────────────────────────────────────────

/** Clear all messaging state. For testing only. */
export function clearAllMessagingState(): void {
  try {
    localStorage.removeItem(DAILY_STATE_KEY);
    localStorage.removeItem(WEEKLY_STATE_KEY);
    localStorage.removeItem(RECOVERY_GAP_KEY);
    localStorage.removeItem(ON_TRACK_SHOWN_KEY);
    localStorage.removeItem(FIRST_PROGRESS_SHOWN_KEY);
    localStorage.removeItem(NORMATIVE_SHOWN_KEY);
    localStorage.removeItem(A1_FIRST_ENTRY_SHOWN_KEY);
    localStorage.removeItem(A1_FIRST_ENTRY_TYPE_KEY);
    localStorage.removeItem(PATTERN_DROP_LOGGING_KEY);
    localStorage.removeItem(PATTERN_DROP_PROTEIN_KEY);
    localStorage.removeItem(PATTERN_DROP_WORKOUT_KEY);
    localStorage.removeItem(PATTERN_DROP_ANY_KEY);
  } catch (error) {
    console.error('[MessagingPersistence] Failed to clear state:', error);
  }
}
